/**
 * #730 — keep an unchanged `GameRow` at the SAME object reference.
 *
 * `getGames()` rebuilds every row of the selected sport on every market
 * publication (~30 Hz): its result cache is stamped by the global `version`,
 * so one unrelated sport's summary frame hands the dashboard 15 fresh row
 * objects whose contents are, for all but the row whose market moved,
 * value-identical to the ones they replace. Every fresh reference invalidates
 * its row's memo, its handler, and every list-keyed memo above it.
 *
 * This is the consumer-owned half of the fix and it is deliberately NOT a
 * cache: nothing is withheld, delayed, or served stale. A freshly built row is
 * compared against the previously adopted one BY VALUE; when they are equal
 * the older reference is handed back because the two are interchangeable.
 * When anything differs the new one wins immediately.
 *
 * ### Unknown compares as CHANGED
 *
 * If `sameGameRow` cannot prove two rows equal — a key it does not know, a
 * shape it does not expect, a value class it has no rule for — it reports them
 * different and the row re-renders. The failure this ticket can most easily
 * cause is a row that skips a render it owed and shows a stale price on a
 * trading screen; that is worse than the slowness being fixed.
 *
 * ### The shape is declared, not discovered
 *
 * #731's `sameVm` derives its field list from the objects and compares ONE
 * level deep, reporting any array as unequal. A `GameRow` is three levels deep
 * (row → `kalshi.away` → `yes[]`), so that comparator would call every row
 * changed on every tick and stabilise nothing. Instead each supported shape is
 * spelled out below as a field map, and TypeScript's `satisfies` pins each
 * map to the type it describes: adding a field to `GameRow` or `VenueBook`
 * fails typecheck here until the map says how to compare it. At runtime a key
 * the map does not list is also "changed", so a shape that drifted past the
 * types (a spread of unknown wire fields, say) still cannot be silently
 * ignored. No arbitrary recursion depth is involved — every nesting the
 * comparator descends into is a named shape.
 *
 * ### Aliasing
 *
 * The comparison is only honest if the OLD row cannot have been mutated in
 * place since it was adopted — two aliases of one mutated object would
 * compare equal while the screen shows the pre-mutation value. `getGames`
 * builds rows, venue books and level arrays fresh on every version
 * (`buildVenueBookFromMarket` maps the wire levels through `toBookLevels`);
 * the store's in-place scalar writes land on its own `HydraMarket` records,
 * which the venue book COPIES. The one shared reference, `priceRanges`, is a
 * wire array the store never mutates. `stableGameRows.test.ts` pins both
 * facts against the real store.
 *
 * ### Idempotent, so render-phase use is safe
 *
 * `stabilize()` runs inside a `useMemo` and updates its own state as it goes.
 * Calling it twice with equal input returns the identical array both times —
 * the second pass compares against what the first chose and proves them
 * equal — so a StrictMode double render or an abandoned concurrent render can
 * at worst reuse a reference whose contents equal the one it replaced.
 */
import type { KalshiPriceRange } from '@/types';
import type { BookLevel, GameRow, SyntheticSpreadOutcome, VenueBook } from './types';

/** How one field is compared. */
type Kind =
  | 'prim'                       // Object.is on a primitive (string/number/boolean/null/undefined)
  | { shape: FieldMap }          // a nested plain object with its own map
  | { list: FieldMap };          // an array of nested plain objects, ordered

type FieldMap = Readonly<Record<string, Kind>>;

const BOOK_LEVEL: FieldMap = {
  priceCents: 'prim',
  size: 'prim',
} satisfies Record<keyof BookLevel, Kind>;

const PRICE_RANGE: FieldMap = {
  start: 'prim',
  end: 'prim',
  step: 'prim',
} satisfies Record<keyof KalshiPriceRange, Kind>;

const VENUE_BOOK: FieldMap = {
  marketTicker: 'prim',
  exchangeIndex: 'prim',
  exchangeIndexState: 'prim',
  nativeSideInverted: 'prim',
  freshness: 'prim',
  sourceValidity: 'prim',
  status: 'prim',
  tokenId: 'prim',
  noTokenId: 'prim',
  conditionId: 'prim',
  negRisk: 'prim',
  tickSize: 'prim',
  priceRanges: { list: PRICE_RANGE },
  priceCents: 'prim',
  lastPriceCents: 'prim',
  yesBidCents: 'prim',
  yesAskCents: 'prim',
  volume: 'prim',
  yesVolume: 'prim',
  noVolume: 'prim',
  volumeIsDepthDerived: 'prim',
  isSharedBinaryBook: 'prim',
  liquidity: 'prim',
  yes: { list: BOOK_LEVEL },
  no: { list: BOOK_LEVEL },
} satisfies Record<keyof VenueBook, Kind>;

const VENUE_PAIR: FieldMap = {
  away: { shape: VENUE_BOOK },
  home: { shape: VENUE_BOOK },
} satisfies Record<keyof NonNullable<GameRow['kalshi']>, Kind>;

const SERIES_META: FieldMap = {
  round: 'prim',
  year: 'prim',
} satisfies Record<keyof NonNullable<GameRow['series']>, Kind>;

const TOURNAMENT_META: FieldMap = {
  eventTicker: 'prim',
} satisfies Record<keyof NonNullable<GameRow['kalshiViaTournament']>, Kind>;

const ARCHIVED_META: FieldMap = {
  kalshiVolume: 'prim',
  polyVolume: 'prim',
} satisfies Record<keyof NonNullable<GameRow['archived']>, Kind>;

const SPREAD_OUTCOME: FieldMap = {
  line: 'prim',
  favored: 'prim',
  kalshiTicker: 'prim',
  polymarketTicker: 'prim',
} satisfies Record<keyof SyntheticSpreadOutcome, Kind>;

const ADVANCE_PROMOTION: FieldMap = {
  advanceQuoted: 'prim',
  advanceKalshiQuoted: 'prim',
  resultKalshiQuoted: 'prim',
} satisfies Record<keyof NonNullable<GameRow['advancePromotion']>, Kind>;

export const GAME_ROW: FieldMap = {
  key: 'prim',
  sport: 'prim',
  date: 'prim',
  timePt: 'prim',
  startTimeMs: 'prim',
  gameState: 'prim',
  gameStateAuthority: 'prim',
  finalWinnerSide: 'prim',
  awayCode: 'prim',
  homeCode: 'prim',
  awayName: 'prim',
  homeName: 'prim',
  awayRotation: 'prim',
  homeRotation: 'prim',
  series: { shape: SERIES_META },
  kalshiViaSeries: { shape: SERIES_META },
  kalshiViaTournament: { shape: TOURNAMENT_META },
  kalshi: { shape: VENUE_PAIR },
  polymarket: { shape: VENUE_PAIR },
  archived: { shape: ARCHIVED_META },
  spreadOutcome: { shape: SPREAD_OUTCOME },
  advancePromotion: { shape: ADVANCE_PROMOTION },
} satisfies Record<keyof GameRow, Kind>;

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  if (v === null || typeof v !== 'object') return false;
  if (Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v) as unknown;
  return proto === Object.prototype || proto === null;
}

function isPrim(v: unknown): boolean {
  return v === null || (typeof v !== 'object' && typeof v !== 'function');
}

/** Two values equal under `kind`. Anything the rule does not cover is false. */
function sameByKind(a: unknown, b: unknown, kind: Kind): boolean {
  if (Object.is(a, b)) return true;
  // An optional field absent on one side and present on the other is a change,
  // including `undefined` vs `null` — the row types mean different things by them.
  if (a == null || b == null) return false;
  if (kind === 'prim') return isPrim(a) && isPrim(b) && Object.is(a, b);
  if ('shape' in kind) return sameShape(a, b, kind.shape);
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (!sameShape(a[i], b[i], kind.list)) return false;
  }
  return true;
}

/** Two plain objects equal under `map`: same key set, every key known, every
 *  value equal under its rule. */
function sameShape(a: unknown, b: unknown, map: FieldMap): boolean {
  if (Object.is(a, b)) return true;
  if (!isPlainRecord(a) || !isPlainRecord(b)) return false;
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    const kind = map[k];
    if (kind === undefined) return false; // a key the map does not know: changed
    if (!sameByKind(a[k], b[k], kind)) return false;
  }
  return true;
}

/** Value equality for one `GameRow`, over the declared shape. */
export function sameGameRow(a: unknown, b: unknown): boolean {
  return sameShape(a, b, GAME_ROW);
}

/**
 * A per-list identity store. One per independently filtered list (the main
 * board and the Saved board each get their own) — sharing one across two
 * lists would let a row released from one be "kept" by the other.
 */
export function createGameRowIdentityStore() {
  let previous = new Map<string, GameRow>();
  let previousList: GameRow[] = [];
  return {
    stabilize(next: readonly GameRow[]): GameRow[] {
      const kept = new Map<string, GameRow>();
      const out: GameRow[] = [];
      for (const row of next) {
        const before = previous.get(row.key);
        const use = before !== undefined && sameGameRow(before, row) ? before : row;
        kept.set(row.key, use);
        out.push(use);
      }
      // Keys absent from `next` are released here: `kept` replaces `previous`.
      previous = kept;
      // The ARRAY's identity matters as much as the rows'. Reuse only when
      // provably interchangeable: same length and every element identity-equal,
      // order included. (#731 measured the alternative: a fresh array per tick
      // rebuilt every downstream handler and defeated the whole optimisation.)
      if (
        previousList.length === out.length &&
        out.every((row, i) => Object.is(previousList[i], row))
      ) {
        return previousList;
      }
      previousList = out;
      return out;
    },
    /** Rows currently held. Exposed so a test can prove rows are released. */
    size(): number {
      return previous.size;
    },
  };
}

export type GameRowIdentityStore = ReturnType<typeof createGameRowIdentityStore>;
